'use client';

import { Alert, AlertDescription } from '@ahafood/ui/components/alert';
import { Button } from '@ahafood/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@ahafood/ui/components/form';
import { Image } from '@ahafood/ui/components/image';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@ahafood/ui/components/input-otp';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useLoginOTP } from '@/api/use-login-by-otp';
import { useRequestOTP } from '@/api/use-request-otp';
import useGoogleRecaptcha from '@/hooks/use-google-recaptcha';
import { useAuth } from '@/providers/auth-provider';

// OTP Schema
const otpSchema = z.object({
  otp: z
    .string()
    .min(6, 'Mã OTP phải có 6 chữ số')
    .max(6, 'Mã OTP phải có 6 chữ số')
    .regex(/^\d{6}$/, 'Mã OTP chỉ được chứa số'),
});

type OTPFormData = z.infer<typeof otpSchema>;

interface CustomOTPFormProps {
  phoneNumber: string;
  onSuccess: () => void;
  onBack: () => void;
}

// Constants
const MAX_ATTEMPTS = 5;
const BLOCK_DURATION = 60 * 60 * 1000;
const TIMER_DURATION = 180;

export function CustomOTPForm({
  phoneNumber,
  onSuccess,
  onBack,
}: CustomOTPFormProps) {
  // States
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeLeft, setBlockTimeLeft] = useState(0);

  const { loginWithOTP } = useAuth();
  const { submitCaptcha } = useGoogleRecaptcha();

  // Storage keys - using global keys instead of phone-specific
  const getStorageKey = (suffix: string) => `otp_global_${suffix}`;

  // API hooks
  const { mutate: loginOTPAPI } = useLoginOTP({
    onSuccess: async (response) => {
      // Reset attempts on successful login
      localStorage.removeItem(getStorageKey('attempts'));
      localStorage.removeItem(getStorageKey('blocked_until'));

      // Complete login process with phone number
      await loginWithOTP(response, phoneNumber, () => {
        setIsLoading(false);
        onSuccess();
      });
    },
    onError: (error: any) => {
      const errorData = error.response?.data || {};
      const errorCode = errorData.error || errorData.title;

      // Xử lý lỗi TOO_MANY_ATTEMPTS từ server
      if (errorCode === 'TOO_MANY_ATTEMPTS' && errorData.blockedUntil) {
        const blockedUntil = errorData.blockedUntil;
        const now = Date.now();

        localStorage.setItem(
          getStorageKey('blocked_until'),
          blockedUntil.toString()
        );
        localStorage.setItem(
          getStorageKey('attempts'),
          MAX_ATTEMPTS.toString()
        );

        setIsBlocked(true);
        setBlockTimeLeft(blockedUntil - now);
        setAttemptCount(MAX_ATTEMPTS);
        setError(errorData.message || 'Bạn đã nhập sai OTP quá nhiều lần.');

        // Redirect to login tab after showing error
        setTimeout(() => {
          onBack();
        }, 2000);
      } else {
        handleOTPError();
      }

      setIsLoading(false);
    },
  });

  const { mutate: requestOTP } = useRequestOTP({
    onSuccess: () => {
      toast.success('Mã OTP mới đã được gửi!');
      startTimer();
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || 'Không thể gửi lại mã OTP';
      toast.error(message);
    },
  });

  const form = useForm<OTPFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: '' },
    mode: 'onChange',
  });

  // Load saved data on mount
  useEffect(() => {
    loadStoredData();
    startTimer();
  }, []);

  // Timer effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isTimerActive && timeLeft > 0) {
      intervalId = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [isTimerActive, timeLeft]);

  // Block countdown effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isBlocked && blockTimeLeft > 0) {
      intervalId = setInterval(() => {
        setBlockTimeLeft((prev) => {
          if (prev <= 1000) {
            // Block expired
            setIsBlocked(false);
            setAttemptCount(0);
            localStorage.removeItem(getStorageKey('attempts'));
            localStorage.removeItem(getStorageKey('blocked_until'));
            return 0;
          }
          return prev - 1000; // Decrease by 1 second
        });
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [isBlocked, blockTimeLeft]);

  // Functions
  const loadStoredData = useCallback(() => {
    try {
      // Load attempt count
      const storedAttempts = localStorage.getItem(getStorageKey('attempts'));
      const attempts = storedAttempts ? parseInt(storedAttempts, 10) : 0;
      setAttemptCount(attempts);

      // Load block status
      const storedBlockedUntil = localStorage.getItem(
        getStorageKey('blocked_until')
      );
      if (storedBlockedUntil) {
        const blockedUntil = parseInt(storedBlockedUntil, 10);
        const now = Date.now();

        if (now < blockedUntil) {
          setIsBlocked(true);
          setBlockTimeLeft(blockedUntil - now);
        } else {
          // Block expired, clear storage
          localStorage.removeItem(getStorageKey('attempts'));
          localStorage.removeItem(getStorageKey('blocked_until'));
        }
      }
    } catch (error) {
      console.error('Error loading stored OTP data:', error);
    }
  }, []);

  const startTimer = useCallback(() => {
    setTimeLeft(TIMER_DURATION);
    setIsTimerActive(true);
  }, []);

  const handleOTPError = useCallback(() => {
    const newAttemptCount = attemptCount + 1;
    setAttemptCount(newAttemptCount);

    // Save attempt count
    localStorage.setItem(getStorageKey('attempts'), newAttemptCount.toString());

    setError('Mã OTP sai hoặc hết hạn, vui lòng nhập lại');

    // Clear OTP input
    form.setValue('otp', '');

    // Check if max attempts reached
    if (newAttemptCount >= MAX_ATTEMPTS) {
      const blockedUntil = Date.now() + BLOCK_DURATION;
      localStorage.setItem(
        getStorageKey('blocked_until'),
        blockedUntil.toString()
      );
      setIsBlocked(true);
      setBlockTimeLeft(BLOCK_DURATION);

      // Redirect to login tab after showing error
      setTimeout(() => {
        onBack();
      }, 2000);
    }
  }, [attemptCount, form, onBack]);

  const handleOTPChange = useCallback(
    (value: string) => {
      if (isBlocked) return;

      form.setValue('otp', value, { shouldValidate: true });
      setError(''); // Clear error when user types

      // Auto submit when 6 digits entered
      if (value.length === 6 && form.formState.isValid) {
        setIsLoading(true);
        loginOTPAPI({ code: value, phone_number: phoneNumber });
      }
    },
    [form, isBlocked, loginOTPAPI, phoneNumber]
  );

  const handleResend = useCallback(async () => {
    if (isBlocked) return;

    setIsResending(true);
    setError('');
    form.reset();

    const recaptcha_token = await submitCaptcha('resend_otp');
    requestOTP({ phone_number: phoneNumber, recaptcha_token });
    setTimeout(() => setIsResending(false), 1500);
  }, [isBlocked, form, requestOTP, phoneNumber, submitCaptcha]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const formatBlockTime = useCallback((milliseconds: number) => {
    const minutes = Math.ceil(milliseconds / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours} giờ ${remainingMinutes} phút`;
    }
    return `${minutes} phút`;
  }, []);

  const formatPhoneDisplay = useCallback((phone: string) => {
    if (phone.startsWith('84')) {
      const withoutPrefix = '0' + phone.slice(2);
      return withoutPrefix.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    }
    return phone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }, []);

  const otpValue = form.watch('otp');
  const isExpired = timeLeft === 0;
  const canResend = timeLeft === 0 && !isTimerActive;

  if (isBlocked) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4 flex">
          <div className="relative size-20 aspect-square -mt-12 z-0">
            <Image
              src={`${assetPrefix}/static/images/robot-authen.png`}
              fill
              alt="Shield Icon"
            />
          </div>
          <div className="space-y-2 flex flex-col bg-red-10 w-full rounded-lg -ml-20 py-2 z-10">
            <h2 className="text-2xl font-bold text-red-60">
              Xác thực không thành công
            </h2>
            <p className="text-neutral-60 text-sm text-center px-4">
              Bạn đã nhập sai mã OTP {MAX_ATTEMPTS} lần liên tiếp. Vì lý do bảo
              mật, bạn không thể tiếp tục đăng nhập/đăng ký. Vui lòng thử lại
              sau{' '}
              <span className="font-bold text-red-60">
                {formatBlockTime(blockTimeLeft)}
              </span>
            </p>
          </div>
        </div>

        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription className="text-center">
            Tài khoản tạm thời bị khóa do nhập sai OTP quá nhiều lần.
            <br />
            Thời gian còn lại: <strong>{formatBlockTime(blockTimeLeft)}</strong>
          </AlertDescription>
        </Alert>

        <Button
          onClick={onBack}
          className="w-full h-12 text-lg bg-red-50 hover:bg-red-60 text-white"
        >
          Tôi đã hiểu
        </Button>
      </div>
    );
  }

  // Normal OTP UI
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4 flex">
        <div className="relative size-20 aspect-square -mt-12 z-0">
          <Image
            src={`${assetPrefix}/static/images/robot-authen.png`}
            fill
            alt="Shield Icon"
          />
        </div>
        <div className="space-y-2 flex flex-col bg-blue-10 w-full rounded-lg -ml-20 py-2 z-10">
          <h2 className="text-2xl font-bold text-secondary-60">Xác thực OTP</h2>
          <p className="text-neutral-60 text-sm">
            Nhập mã OTP 6 chữ số được gửi đến
            <br />
            <span className="font-semibold text-secondary-60">
              {formatPhoneDisplay(phoneNumber)}
            </span>
          </p>
        </div>
      </div>

      {/* Timer and attempts info */}
      <div className="text-center space-y-2">
        {/* <div className="text-sm text-neutral-60">
          {isExpired ? (
            <span className="text-red-60 flex items-center justify-center gap-1">
              <AlertTriangle className="size-4" />
              Mã OTP đã hết hạn
            </span>
          ) : (
            <span>
              Mã có hiệu lực trong: <strong>{formatTime(timeLeft)}</strong>
            </span>
          )}
        </div> */}

        {attemptCount > 0 && (
          <div className="text-xs text-neutral-50">
            Số lần thử: {attemptCount}/{MAX_ATTEMPTS}
          </div>
        )}
      </div>

      {/* OTP Form */}
      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          <FormField
            control={form.control}
            name="otp"
            render={({ field }) => (
              <FormItem className="flex flex-col items-center space-y-4">
                <FormControl>
                  <InputOTP
                    maxLength={6}
                    value={field.value}
                    onChange={handleOTPChange}
                    disabled={isLoading || isExpired || isBlocked}
                    className="gap-3"
                  >
                    <InputOTPGroup>
                      {Array.from({ length: 6 }, (_, index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className="size-14 text-xl font-bold border-2 border-neutral-30 focus:border-blue-50 focus:ring-2 focus:ring-blue-20 transition-all duration-200"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="space-y-4">
            {/* Resend Button */}
            <div className="text-center">
              {canResend || isExpired ? (
                <Button
                  type="button"
                  variant="link"
                  onClick={handleResend}
                  disabled={isResending || isLoading || isBlocked}
                  className="p-0 h-auto font-normal text-blue-60 hover:text-blue-70"
                >
                  {isResending ? (
                    <>
                      <RefreshCw className="size-4 mr-1 animate-spin" />
                      Đang gửi lại...
                    </>
                  ) : (
                    'Gửi lại mã OTP'
                  )}
                </Button>
              ) : (
                <span className="text-sm text-neutral-60">
                  Gửi lại sau {formatTime(timeLeft)}
                </span>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="button"
              onClick={() => {
                if (otpValue.length === 6 && form.formState.isValid) {
                  setIsLoading(true);
                  loginOTPAPI({ code: otpValue, phone_number: phoneNumber });
                }
              }}
              disabled={
                otpValue.length !== 6 ||
                isLoading ||
                isExpired ||
                isBlocked ||
                !form.formState.isValid
              }
              className="w-full h-12 text-lg bg-gradient-to-r from-blue-50 to-blue-60 hover:from-blue-60 hover:to-blue-70 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 size-5 animate-spin" />
                  Đang xác thực...
                </>
              ) : (
                'Xác thực OTP'
              )}
            </Button>

            {/* Back Button */}
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              disabled={isLoading}
              className="w-full h-10 text-neutral-50 hover:text-neutral-70 underline"
            >
              <ArrowLeft className="mr-2 size-4" />
              Quay lại nhập số điện thoại
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
